import React, { useEffect, useRef, useState } from 'react';
import ServiceOrdersAPI, { AdminServiceOrderLookup } from '../../api/serviceOrders';

/**
 * Payroll editor — service order combobox for the 2020 Hourly modal's Service
 * Order mode. A clone of ProjectPicker over `admin/serviceOrders/typeahead`
 * (250 ms debounce, 2-char minimum, sequence guard). SOs whose job is closed
 * (`projectActive === false`) render dimmed with a "Job closed" tag and cannot
 * be picked.
 *
 * Keyboard: while the dropdown is open, Enter/Escape/arrows are the PICKER's
 * (stopPropagation) so they never trigger the modal's save/cancel contract.
 */

export interface PickedServiceOrder {
  serviceOrderID: number;
  altServiceOrderID: string;
  name: string | null;
  projectID: number | null;
  altProjectID: string | null;
  projectName: string | null;
  projectActive: boolean;
}

export interface ServiceOrderPickerProps {
  value: PickedServiceOrder | null;
  onChange: (serviceOrder: PickedServiceOrder) => void;
  disabled?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  bad?: boolean;
}

/** 2020 SOs carry no name — `SO 4412 · 24-118 Sunrise Ridge`. */
export const serviceOrderLabel = (so: {
  altServiceOrderID: string; altProjectID: string | null; projectName: string | null;
}) => `SO ${so.altServiceOrderID} · ${[so.altProjectID, so.projectName].filter(Boolean).join(' ')}`.trim();

export const toPickedServiceOrder = (so: AdminServiceOrderLookup): PickedServiceOrder => ({
  serviceOrderID: so.serviceOrderID,
  altServiceOrderID: so.altServiceOrderID,
  name: so.name,
  projectID: so.projectID,
  altProjectID: so.altProjectID,
  projectName: so.projectName,
  projectActive: so.projectActive,
});

const ServiceOrderPicker: React.FC<ServiceOrderPickerProps> = ({
  value, onChange, disabled = false, placeholder = 'Search service orders…',
  autoFocus = false, bad = false,
}) => {
  const [text, setText] = useState(value ? serviceOrderLabel(value) : '');
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<PickedServiceOrder[]>([]);
  const [highlight, setHighlight] = useState(0);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<number | null>(null);
  const seqRef = useRef(0);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setText(value ? serviceOrderLabel(value) : '');
  }, [value]);

  // Close on outside click.
  useEffect(() => {
    const onDoc = (ev: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(ev.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const runSearch = (q: string) => {
    const seq = ++seqRef.current;
    setSearching(true);
    ServiceOrdersAPI.typeahead(q, 25)
      .then((rows) => {
        if (seq !== seqRef.current) return;
        setOptions((rows ?? []).map(toPickedServiceOrder));
        setHighlight(0);
      })
      .catch(() => {
        if (seq === seqRef.current) setOptions([]);
      })
      .finally(() => {
        if (seq === seqRef.current) setSearching(false);
      });
  };

  const onInput = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const q = ev.target.value;
    setText(q);
    setOpen(true);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (q.trim().length < 2) {
      setOptions([]);
      return;
    }
    debounceRef.current = window.setTimeout(() => runSearch(q.trim()), 250);
  };

  const pickable = (so: PickedServiceOrder) => so.projectActive !== false;

  const pick = (so: PickedServiceOrder) => {
    if (!pickable(so)) return;
    onChange(so);
    setText(serviceOrderLabel(so));
    setOpen(false);
  };

  const onKeyDown = (ev: React.KeyboardEvent) => {
    if (!open) {
      if (ev.key === 'ArrowDown') {
        setOpen(true);
        ev.preventDefault();
        ev.stopPropagation();
      }
      return;
    }
    if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
      ev.preventDefault();
      ev.stopPropagation();
      setHighlight((h) => {
        const n = options.length;
        if (n === 0) return 0;
        return ev.key === 'ArrowDown' ? Math.min(h + 1, n - 1) : Math.max(h - 1, 0);
      });
    } else if (ev.key === 'Enter') {
      ev.preventDefault();
      ev.stopPropagation();
      if (options.length > 0) pick(options[Math.min(highlight, options.length - 1)]);
      else setOpen(false);
    } else if (ev.key === 'Escape') {
      ev.preventDefault();
      ev.stopPropagation();
      setOpen(false);
      setText(value ? serviceOrderLabel(value) : '');
    }
  };

  return (
    <div className="pe-picker" ref={rootRef}>
      <input
        className={`pe-in sans${bad ? ' bad' : ''}`}
        value={text}
        onChange={onInput}
        onFocus={(ev) => ev.target.select()}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        autoComplete="off"
        spellCheck={false}
      />
      {open && (
        <div className="pe-picker-menu" role="listbox">
          {options.map((so, i) => {
            const closed = !pickable(so);
            return (
              <button
                key={so.serviceOrderID}
                type="button"
                role="option"
                aria-selected={value?.serviceOrderID === so.serviceOrderID}
                aria-disabled={closed}
                className={`pe-picker-opt${highlight === i ? ' hl' : ''}${closed ? ' off' : ''}`}
                onMouseDown={(ev) => { ev.preventDefault(); pick(so); }}
                title={closed ? 'Job closed — pick a service order on an open job' : undefined}
              >
                <span className="mono">SO {so.altServiceOrderID}</span>
                {' '}
                {[so.altProjectID, so.projectName].filter(Boolean).join(' ')}
                {closed && <span className="pe-tag">Job closed</span>}
              </button>
            );
          })}
          {options.length === 0 && (
            <div className="pe-picker-empty">
              {searching
                ? 'Searching…'
                : text.trim().length < 2
                  ? 'Type to search service orders'
                  : 'No matching service orders (synced from Sage)'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ServiceOrderPicker;
